import type { Permissao } from '@/stores/usePerfisStore';

export interface Feature {
  base: string;
  label: string;
  readPerm?: Permissao;
  writePerm?: Permissao;
}

export type Acao = 'read' | 'write';

const MODULOS_NA_ORDEM_DO_MENU = [
  'Trabalhador',
  'Saúde e Segurança',
  'Folha de Pagamento',
  'Encargos',
  'eGov',
  'Certidões',
  'Arquivos',
  'eSocial',
];

const FUNCIONALIDADES_NA_ORDEM_DO_MENU = [
  'egov:trabalhador.afastamento',
  'egov:trabalhador.cbo',
  'egov:trabalhador.horarios',
  'egov:saude.agentes-nocivos',
  'egov:folha.rubricas',
  'egov:encargos.dctf',
  'egov:encargos.mensagens-ecac',
  'egov:egov.cessao',
  'egov:certidoes.cnd',
  'egov:certidoes.crf',
  'egov:certidoes.cndt',
  'egov:config.documentos',
  'egov:arquivos.exportacoes',
  'egov:config.esocial-importacao',
  'egov:reprocessamento',
  'egov:config.tabelas',
  'egov:config.parametros-analise',
];

function posicao(ordem: readonly string[], chave: string): number {
  const i = ordem.indexOf(chave);
  return i === -1 ? ordem.length : i;
}

export function agrupar(permissoes: Permissao[]): Record<string, Feature[]> {
  const groups: Record<string, Permissao[]> = {};
  for (const p of permissoes) {
    const g = p.group || 'Outros';
    (groups[g] ||= []).push(p);
  }
  const nomesDosGrupos = Object.keys(groups).sort(
    (a, b) =>
      posicao(MODULOS_NA_ORDEM_DO_MENU, a) - posicao(MODULOS_NA_ORDEM_DO_MENU, b) ||
      a.localeCompare(b),
  );
  const out: Record<string, Feature[]> = {};
  for (const g of nomesDosGrupos) {
    const feats: Record<string, Feature> = {};
    for (const perm of groups[g].sort((a, b) => a.code.localeCompare(b.code))) {
      const base = perm.code.replace(/:read$|:write$/, '');
      const feat = (feats[base] ||= {
        base,
        label: (perm.display_name || base).replace(/ - (Visualizar|Editar)$/i, ''),
      });
      if (perm.code.endsWith(':read')) feat.readPerm = perm;
      else if (perm.code.endsWith(':write')) feat.writePerm = perm;
      else feat.readPerm = perm;
    }
    out[g] = Object.values(feats).sort(
      (a, b) =>
        posicao(FUNCIONALIDADES_NA_ORDEM_DO_MENU, a.base) -
          posicao(FUNCIONALIDADES_NA_ORDEM_DO_MENU, b.base) || a.base.localeCompare(b.base),
    );
  }
  return out;
}

export function aplicarMarcacao(
  selecionados: ReadonlySet<string>,
  feats: Feature[],
  acao: Acao,
  marcar: boolean,
): Set<string> {
  const next = new Set(selecionados);
  for (const f of feats) {
    if (acao === 'write') {
      if (!f.writePerm) continue;
      if (marcar) {
        next.add(f.writePerm.id);
        if (f.readPerm) next.add(f.readPerm.id);
      } else {
        next.delete(f.writePerm.id);
      }
    } else {
      if (!f.readPerm) continue;
      if (marcar) {
        next.add(f.readPerm.id);
      } else {
        next.delete(f.readPerm.id);
        if (f.writePerm) next.delete(f.writePerm.id);
      }
    }
  }
  return next;
}
